import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { CreativeModelFieldsProps } from "@/types/creative-library";

/** Name and type of a new model. Fields only: the host owns the footer and the create call. */
export function CreativeModelFields({
  name,
  kind,
  disabled,
  onNameChange,
  onKindChange,
  onSubmit,
}: CreativeModelFieldsProps) {
  const { t } = useTranslation("app");
  const nameInput = useRef<HTMLInputElement>(null);
  // A later task than the one where a closing Select hands focus back to its trigger.
  useEffect(() => {
    const timer = window.setTimeout(() => nameInput.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, []);
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="flex min-w-0 flex-col gap-2">
        <Label htmlFor="model-create-name">{t("creativeLibrary.name")}</Label>
        <Input
          ref={nameInput}
          id="model-create-name"
          value={name}
          maxLength={255}
          disabled={disabled}
          onChange={(event) => onNameChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.nativeEvent.isComposing) {
              event.preventDefault();
              onSubmit();
            }
          }}
          data-testid="model-create-name"
        />
      </div>
      <div className="flex min-w-0 flex-col gap-2">
        <Label>{t("creativeLibrary.subjectKind")}</Label>
        <Select
          value={kind}
          disabled={disabled}
          onValueChange={(value) => onKindChange(value as typeof kind)}
        >
          <SelectTrigger
            aria-label={t("creativeLibrary.subjectKind")}
            data-testid="model-create-type"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="person">
              {t("creativeLibrary.subjectKinds.person")}
            </SelectItem>
            <SelectItem value="mascot">
              {t("creativeLibrary.subjectKinds.mascot")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
